import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { qdfAPI } from '../../services/api';
import QDFFormHeader from './components/QDFFormHeader';
import MaintenanceTypeSelector from './components/MaintenanceTypeSelector';
import PreventiveMaintenanceForm from './forms/PreventiveMaintenanceForm';
import ServiceRequestForm from './forms/ServiceRequestForm';
import useQDFForm from './hooks/useQDFForm';
import useQDFShopConfig from './hooks/useQDFShopConfig';
import { generatePDF } from './services/pdfService';
import { generatePDFFileName } from './utils/qdfUtils';
import { PageLoader, Alert } from '../../components/ui';

// Keep the wrapper at module scope so form updates preserve input focus
// and local form state instead of remounting the entire subtree.
const Shell = ({ children }) => (
  <div className="min-h-[100dvh] bg-background">
    <div className="mx-auto w-full max-w-4xl px-4 py-6 pb-[calc(var(--bottom-nav-height)+env(safe-area-inset-bottom,0px)+1rem)] sm:px-6 lg:pb-10">
      <div className="rounded-lg border border-border bg-surface p-4 shadow-sm sm:p-6 lg:p-8">
        {children}
      </div>
    </div>
  </div>
);

const QDFPPMForm = () => {
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useParams();
  const isPublicMode = Boolean(token);
  const [publicPrefill, setPublicPrefill] = useState(null);
  const [publicLoading, setPublicLoading] = useState(isPublicMode);
  const [publicError, setPublicError] = useState('');
  const [publicReferenceNumber, setPublicReferenceNumber] = useState('');
  const { config: shopConfig, loading: shopConfigLoading } = useQDFShopConfig();
  
  // Check if we're editing an existing report from navigation state
  const editReport = isPublicMode ? null : location.state?.editReport;
  const isEditing = !!editReport;
  
  const {
    maintenanceType,
    formData,
    loading,
    generatingNumber,
    setMaintenanceType,
    setLoading,
    handleChange,
    refreshDocumentNumber,
    resetForm
  } = useQDFForm('corrective', editReport, {
    isPublicMode,
    publicPrefill,
    shopConfig,
  });

  useEffect(() => {
    const verifyPublicToken = async () => {
      if (!isPublicMode || !token) {
        return;
      }

      setPublicLoading(true);
      setPublicError('');
      try {
        const response = await qdfAPI.verifyPublicToken(token);
        setPublicPrefill(response?.data?.prefill || null);
      } catch (error) {
        setPublicError(
          error?.response?.data?.message ||
          'This QR code is invalid or expired. Please contact QDF support.'
        );
      } finally {
        setPublicLoading(false);
      }
    };

    verifyPublicToken();
  }, [isPublicMode, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Field name mapping for user-friendly error messages
    const fieldLabels = {
      scNumber: 'Document Number',
      scStatus: 'Status',
      priority: 'Priority',
      scDescription: 'Description',
      location: 'Location',
      ppmType: 'PPM Type',
      assetNumber: 'Asset Number',
      assetDescription: 'Asset Description',
      assetTag: 'Asset Tag',
      department: 'Department',
      zone: 'Zone',
      requesterName: 'Requester Name',
      requesterContact: 'Contact Number',
      contactNumber: 'Contact Number',
      creationDate: 'Creation Date',
      targetResponse: 'Target Response Date',
      targetResolution: 'Target Resolution Date',
      actualDate: 'Actual Date',
      actualResolution: 'Actual Resolution Date',
      contractor: 'Contractor',
      weekNumber: 'Week Number',
      maintenanceType: 'Maintenance Type'
    };

    // Function to make error messages user-friendly
    const makeErrorUserFriendly = (errorMessage) => {
      if (!errorMessage) return errorMessage;
      
      let friendlyMessage = errorMessage;
      
      // Replace technical field names with user-friendly labels
      Object.keys(fieldLabels).forEach(fieldName => {
        const regex = new RegExp(`\\b${fieldName}\\b`, 'gi');
        friendlyMessage = friendlyMessage.replace(regex, fieldLabels[fieldName]);
      });
      
      // Replace common technical terms
      friendlyMessage = friendlyMessage.replace(/validation error/gi, 'Required field missing');
      friendlyMessage = friendlyMessage.replace(/is required/gi, 'is required');
      friendlyMessage = friendlyMessage.replace(/Path `/gi, '');
      friendlyMessage = friendlyMessage.replace(/` is required/gi, ' is required');
      
      return friendlyMessage;
    };
    
    try {
      // Client-side validation
      const requiredFields = isPublicMode
        ? ['creationDate', 'priority', 'scDescription', 'requesterName', 'requesterContact']
        : ['scNumber', 'scStatus', 'priority', 'scDescription', 'location'];
      
      // Add maintenance type specific required fields
      if (!isPublicMode && maintenanceType === 'preventive') {
        requiredFields.push('ppmType');
        if (formData.ppmType !== 'Monthly') {
          requiredFields.push('assetNumber', 'assetDescription', 'assetTag', 'department');
        } else {
          requiredFields.push('zone');
        }
      } else if (!isPublicMode) {
        requiredFields.push('assetNumber', 'assetDescription', 'assetTag', 'department', 'requesterName', 'requesterContact');
      }
      
      // Check for missing required fields
      const missingFields = requiredFields.filter(field => {
        const value = formData[field];
        return !value || value.trim() === '';
      });
      
      if (missingFields.length > 0) {
        // Convert field names to user-friendly labels
        const missingLabels = missingFields.map(field => fieldLabels[field] || field);
        toast.error(`Please fill in all required fields: ${missingLabels.join(', ')}`);
        setLoading(false);
        return;
      }
      
      // Helper function to convert datetime-local to ISO string (preserving local time as intended)
      const convertToISODate = (dateTimeLocal) => {
        if (!dateTimeLocal) return null;
        try {
          // datetime-local values are in format YYYY-MM-DDTHH:MM
          // We want to treat this as local time and convert to ISO
          const date = new Date(dateTimeLocal);
          return date.toISOString();
        } catch (e) {
          return null;
        }
      };

      if (isPublicMode) {
        const publicPayload = {
          creationDate: convertToISODate(formData.creationDate) || new Date().toISOString(),
          scDescription: formData.scDescription,
          priority: formData.priority,
          requesterName: formData.requesterName,
          requesterContact: formData.requesterContact
        };

        const response = await qdfAPI.submitPublicReport(token, publicPayload);
        const referenceNumber = response?.data?.referenceNumber || response?.data?.report?.scNumber || '';

        if (referenceNumber) {
          setPublicReferenceNumber(referenceNumber);
          toast.success(`Service request submitted. Reference Number: ${referenceNumber}`);
        } else {
          toast.success('Service request submitted successfully.');
        }

        await resetForm();
        return;
      }

      // Prepare the data to save to the backend
      const reportData = {
        weekNumber: formData.weekNumber,
        scNumber: formData.scNumber,
        scStatus: formData.scStatus,
        creationDate: convertToISODate(formData.creationDate) || new Date().toISOString(),
        contractor: formData.contractor || 'RGA',
        priority: formData.priority,
        scDescription: formData.scDescription,
        maintenanceType: maintenanceType,
        // Convert datetime-local values to ISO dates
        targetResponse: convertToISODate(formData.targetResponse),
        targetResolution: convertToISODate(formData.targetResolution),
        // Location is always required
        location: formData.location
      };

      // Add fields based on maintenance type
      if (maintenanceType === 'preventive') {
        reportData.ppmType = formData.ppmType;
        if (formData.ppmType === 'Monthly') {
          reportData.zone = formData.zone;
        } else {
          reportData.assetNumber = formData.assetNumber;
          reportData.assetDescription = formData.assetDescription;
          reportData.assetTag = formData.assetTag;
          reportData.department = formData.department;
        }
      } else {
        // Service Request
        reportData.assetNumber = formData.assetNumber;
        reportData.assetDescription = formData.assetDescription;
        reportData.assetTag = formData.assetTag;
        reportData.department = formData.department;
        reportData.requesterName = formData.requesterName;
        reportData.contactNumber = formData.requesterContact;
        // Include actual dates only for service requests
        reportData.actualDate = convertToISODate(formData.actualDate);
        reportData.actualResolution = convertToISODate(formData.actualResolution);
      }

      // Remove any undefined or empty string values except for required fields
      Object.keys(reportData).forEach(key => {
        if (reportData[key] === '' || reportData[key] === undefined) {
          delete reportData[key];
        }
      });
      
      // Save the data to the backend
      let saveResponse;
      if (isEditing) {
        // Update existing report
        saveResponse = await qdfAPI.updateReport(editReport._id, reportData);
      } else {
        // Create new report
        saveResponse = await qdfAPI.createReport(reportData);
      }
      
      if (saveResponse.data.success || saveResponse.data.message) {
        // Only generate/download the PDF when the work order is Closed.
        // PDF export only applies to preventive maintenance (PPM) here; for
        // service requests the PDF is generated from the work order modal.
        // Creating or editing while Active/On Hold/Cancelled just saves the
        // record; the PDF is produced when the user later edits the record
        // and sets the status to Closed.
        const isClosed = formData.scStatus === 'Closed';
        let pdfGenerated = false;

        if (isClosed && maintenanceType === 'preventive') {
          const pdfBytes = await generatePDF(formData, maintenanceType);

          if (pdfBytes) {
            const fileName = generatePDFFileName(formData, maintenanceType);
            const pdfBlob = new Blob([pdfBytes], { type: 'application/pdf' });
            const url = URL.createObjectURL(pdfBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            link.click();
            URL.revokeObjectURL(url);
            pdfGenerated = true;
          }
        }

        if (isEditing) {
          toast.success(
            pdfGenerated
              ? 'Report closed and PDF generated successfully!'
              : 'Report updated successfully!'
          );
        } else {
          toast.success(
            pdfGenerated
              ? 'Report saved and PDF generated successfully!'
              : maintenanceType === 'preventive'
                ? 'Report saved successfully! PDF will be generated when the status is set to Closed.'
                : 'Report saved successfully!'
          );
        }
        
        if (!isEditing) {
          // Reset the entire form for next submission (only for new reports)
          setTimeout(async () => {
            await resetForm();
          }, 500);
        }
        
        // Navigate back to main page
        setTimeout(() => {
          navigate('/qdf-main', { state: { refresh: true } });
        }, 2000);
      } else {
        toast.error(isEditing ? 'Failed to update report' : 'Failed to save report to system');
      }
    } catch (error) {
      console.error('Error saving report or generating PDF:', error);
      console.error('Error response data:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error message:', error.message);
      
      if (error.response?.status === 404) {
        toast.error('QDF service is not available. Please try again later.');
      } else if (error.response?.status === 400) {
        const errorMsg = makeErrorUserFriendly(error.response?.data?.message || 'Bad request');
        toast.error(`Save failed: ${errorMsg}`);
      } else if (error.response?.status === 500) {
        toast.error('Server error. Please try again later.');
      } else if (error.response?.data?.message) {
        const errorMsg = makeErrorUserFriendly(error.response.data.message);
        toast.error(`Failed to save: ${errorMsg}`);
      } else if (error.response?.data?.error) {
        const errorMsg = makeErrorUserFriendly(error.response.data.error);
        toast.error(`Validation error: ${errorMsg}`);
      } else if (error.message?.includes('SC Number already exists') || error.response?.data?.message?.includes('SC Number already exists')) {
        toast.error('This Document Number already exists. Generating a new one...');
        // Automatically generate a new document number and retry
        await refreshDocumentNumber();
        return; // Don't set loading to false, let user try again with new number
      } else if (error.name === 'NetworkError' || error.message.includes('Network')) {
        toast.error('Network error. Please check your connection and try again.');
      } else {
        const errorMsg = makeErrorUserFriendly(error.message || 'Unknown error');
        toast.error(`Failed to save report: ${errorMsg}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (isPublicMode) {
      resetForm();
      return;
    }

    if (!isEditing) {
      // If we're creating a new report, clear the context to go to page 1
      sessionStorage.removeItem('qdf-return-context');
    }
    // If we're editing, keep the context so restoration works properly
    navigate('/qdf-main');
  };

  if (isPublicMode && publicLoading) {
    return (
      <Shell>
        <QDFFormHeader title="QDF Service Request" showBack={false} />
        <PageLoader label="Validating QR code…" />
      </Shell>
    );
  }

  if (isPublicMode && publicError) {
    return (
      <Shell>
        <QDFFormHeader title="QDF Service Request" showBack={false} />
        <Alert variant="error" title="This link can't be used">
          {publicError}
        </Alert>
      </Shell>
    );
  }

  if (!isPublicMode && shopConfigLoading) {
    return (
      <Shell>
        <QDFFormHeader title="QDF Form" showBack={false} />
        <PageLoader label="Loading shop configuration…" />
      </Shell>
    );
  }

  return (
    <Shell>
      <QDFFormHeader
        onBack={handleCancel}
        isEditing={isEditing}
        showBack={!isPublicMode}
        title={isPublicMode ? 'QDF Service Request' : undefined}
      />

      {isPublicMode && publicPrefill?.shopLabel && (
        <div className="mb-6 space-y-3">
          <Alert variant="info">
            Shop: <span className="font-semibold">{publicPrefill.shopLabel}</span>
          </Alert>
          {publicReferenceNumber && (
            <Alert variant="success" title="Request submitted">
              Reference number: <span className="tabular font-semibold">{publicReferenceNumber}</span>
            </Alert>
          )}
        </div>
      )}

      {!isPublicMode && (
        <MaintenanceTypeSelector
          maintenanceType={maintenanceType}
          setMaintenanceType={setMaintenanceType}
        />
      )}

      {maintenanceType === 'preventive' ? (
        <PreventiveMaintenanceForm
          formData={formData}
          handleChange={handleChange}
          handleSubmit={handleSubmit}
          loading={loading}
          generatingNumber={generatingNumber}
          onCancel={handleCancel}
          isEditing={isEditing}
          shopConfig={shopConfig}
        />
      ) : (
        <ServiceRequestForm
          formData={formData}
          handleChange={handleChange}
          handleSubmit={handleSubmit}
          loading={loading}
          generatingNumber={generatingNumber}
          onCancel={handleCancel}
          isEditing={isEditing}
          isPublicMode={isPublicMode}
          shopConfig={shopConfig}
        />
      )}
    </Shell>
  );
};

export default QDFPPMForm;
